<script setup>
import {onMounted, reactive, ref} from 'vue'
import {Feedback, State, clone, go, unique} from '@/paks/vu-app'
import {SelectDisplay, getDisplay, persistDisplay, updateDisplay} from '@/paks/vu-dash'

const props = defineProps({})

const emit = defineEmits(['input'])

const page = reactive({
    displayName: null,
    display: {},
    displays: [],
    features: [],
    groups: [],
})

const Features = [
    {group: 'Authentication', path: 'auth.login', type: 'checkbox', tip: 'Login required to use app'},
    {group: 'Authentication', path: 'auth.forgot', type: 'checkbox', tip: 'Forgot password recovery'},
    {group: 'Authentication', path: 'auth.register', type: 'checkbox', tip: 'Enable user registration'},

    // To show claim/release options
    {group: 'Cloud', path: 'cloud.enable', type: 'checkbox', tip: 'Enable cloud-based management'},

    {group: 'Dashboards', path: 'dash.enable', type: 'checkbox', tip: 'Enable user dashboards'},
    {group: 'Dashboards', path: 'dash.edit', type: 'checkbox', tip: 'Enable editing via toolbar widget'},
    {group: 'Dashboards', path: 'dash.database', type: 'checkbox', tip: 'Enable database access'},
    {group: 'Dashboards', path: 'dash.metrics', type: 'checkbox', tip: 'Enable metrics'},
    {group: 'Dashboards', path: 'dash.multiple', type: 'checkbox', tip: 'Support multiple dashboards'},

    // Always need sidebar
    {group: 'Navigation', path: 'nav.sidebarHide', type: 'checkbox', tip: 'Auto hide sidebar when not active'},

    {group: 'Navigation', path: 'nav.alerts', type: 'checkbox', tip: 'Display alert notifications in navbar'},
    {group: 'Navigation', path: 'nav.dark', type: 'checkbox', tip: 'Display dark theme option in navbar'},
    {group: 'Navigation', path: 'nav.login', type: 'checkbox', tip: 'Display user login and submenu'},
    {group: 'Navigation', path: 'nav.profile', type: 'checkbox', tip: 'Display user account profile link'},
    {group: 'Navigation', path: 'nav.settings', type: 'checkbox', tip: 'Display user account settings link'},
    {group: 'Navigation', path: 'nav.cookies', type: 'checkbox', tip: 'Display navigation cookies in tabbed pages'},
    {group: 'Navigation', path: 'nav.doc', type: 'checkbox', tip: 'Display documentation link in navbar'},
    {group: 'Navigation', path: 'nav.help', type: 'checkbox', tip: 'Support help panel'},

    {group: 'Widgets', path: 'widget.actions', type: 'checkbox', tip: 'Enable widget actions'},
    {group: 'Widgets', path: 'widget.multiCloud', type: 'checkbox', tip: 'Widgets should support multiple clouds'},
]

//  Component refs
const primary = ref(null)
const confirm = ref(null)

onMounted(async () => {
    page.displayName = State.app.display.name
    page.displays = State.app.fullDisplay.displays || []
    changeDisplay(page.displayName)
})

function changeDisplay(displayName) {
    page.displayName = displayName
    page.display = getDisplay(displayName)
    page.views = clone(page.display.views)
    page.features = mapFeatures(Features)
    page.groups = unique(Features.map((f) => f.group))
}

function mapFeatures(features) {
    if (page.display.features.auth?.register == null) {
        page.display.features.auth.register = true
    }
    features = clone(features)
    for (let item of features) {
        let value = page.display.features
        for (let prop of item.path.split('.')) {
            value = value[prop]
            if (!value) break
        }
        item.value = value
    }
    return features
}

async function save() {
    let features = clone(page.display.features)
    for (let item of page.features) {
        let feature = features
        let leaf
        let parts = item.path.split('.')
        for (let prop of parts) {
            leaf = feature
            feature = feature[prop]
        }
        if (leaf) {
            leaf[parts.at(-1)] = item.value
        }
    }
    page.display.features = features
    await updateDisplay(page.display)
    await persistDisplay()
    Feedback.info('Please wait a minute for the app to be deployed')
    emit('input')
}

function cancel() {
    go({name: 'pages'})
}

function clicked(item) {
    if (item.path == 'dash.enable' && !item.value) {
        for (let feature of page.features) {
            if (feature.path.indexOf('dash') == 0) {
                feature.value = false
            }
        }
    } else if (item.path.indexOf('dash.') == 0 && item.value) {
        page.features.find((f) => f.path == 'dash.enable').value = true
    }
}
</script>

<template>
    <div class="view page feature-edit">
        <SelectDisplay v-if="page.displays.length > 1" v-model="page.displayName" @input="changeDisplay" />

        <vu-form cols="12" help="/doc/ui/view/feature-edit.html" ref="primary" :data="page" :save="save">
            <div class="actions">
                <v-btn size="small" color="primary" type="submit" :loading="page.saving">Deploy</v-btn>
                <v-btn size="small" color="none" @click="cancel">Cancel</v-btn>
            </div>

            <div class="cards">
                <v-card v-for="group of page.groups" class="my-2 _vcol" :class="State.app.mobile ? '_vcol-12' : '_vcol-6'">
                    <v-card-title class="groups">{{ group }}</v-card-title>
                    <v-card-text>
                        <div v-for="item of page.features.filter((f) => f.group == group)" class="vrow">
                            <vu-input
                                v-model="item.value"
                                :type="item.type"
                                :cols="6"
                                :label="`${item.tip} (${item.path})`"
                                hide-details
                                @change="clicked(item)" />
                        </div>
                    </v-card-text>
                </v-card>
            </div>
            <vu-confirm ref="confirm" />
        </vu-form>
    </div>
</template>

<style lang="scss">
.feature-edit {
    font-size: 0.9rem;
    margin-bottom: 40px;
    .vu-form {
        width: unset !important;
        margin-top: 16px !important;
        padding: 0 !important;
        column-gap: 12px;
        .v-input,
        .v-label {
            font-size: 0.9rem;
        }
        .cards {
            display: flex;
            flex-wrap: wrap;
            column-gap: 12px;
        }
        .v-card {
            flex: 0 0 calc(50% - 6px);
            box-sizing: border-box;
            background-color: rgb(var(--v-theme-background-lighten-1));
            border: 1px solid rgb(var(--v-theme-accent));
        }
        @media (max-width: 640px) {
            .v-card {
                flex: 0 0 100%;
            }
        }
        .v-card.vcol-6:last-of-type {
            flex: 1 1 100% !important;
        }
        .v-card-title {
            font-size: 1.4em;
        }
        .groups {
            font-size: 1.2em;
        }
        .v-select-list {
            padding: 0;
            border: solid 1px rgb(var(--v-theme-border-lighten-1));
            border-radius: 0;
        }
        .v-selection-control {
            min-height: unset !important;
        }
        h3 {
            font-weight: normal;
            color: rgb(var(--v-theme-text));
        }
    }
    .actions {
        margin: 0 0 8px 2px !important;
        .v-btn {
            margin-right: 10px;
        }
    }
}
</style>
